import { action, observable, runInAction } from 'mobx';

import { jwtStore, selfEvaluationEditorStore } from '.';
import request from '../common/utils/Request';

import { Gender, UserType } from '../types';

class UserInfoStore {
  private static _instance: UserInfoStore;

  @observable public awaitServer: boolean = false;
  @observable public city: string = '';
  @observable public currentRenderId = 0;
  @observable public gender: Gender = Gender.OTHER;
  @observable public interests: string[] = [];
  @observable public state: string = '';
  @observable public userType: UserType = UserType.MENTEE;

  /**
   * Use UserInfoStore.instance to retrieve an instance of this class.
   */
  private constructor() { }

  /**
   * Instance of UserInfoStore.
   */
  public static get instance() {
    if (!this._instance) {
      this._instance = new UserInfoStore();
    }

    return this._instance;
  }

  /**
   * Submit all user info data to server and retrieve a brand new JWT.
   */
  @action
  public submitUserInfo = async () => {
    this.awaitServer = true;

    await Promise.all([ this.submitBasicInfo(), this.submitSelfEvaluations() ]);

    // Finalise update
    await request.post('/me/done-update/');

    // Get new token
    await jwtStore.refresh();
    runInAction(() => this.awaitServer = false);
  }

  /**
   * Submit basic info to the server.
   */
  private submitBasicInfo = () => {
    const interests = Array.isArray(this.interests) ? this.interests.join(',') : this.interests;
    const userProfilePayload = {
      city: this.city,
      gender: this.gender.substr(0, 1),
      interests,
      questions: selfEvaluationEditorStore.question,
      state_province: this.state
    };

    // Filter out unnecessary payload.
    const filteredPayload = Object.keys(userProfilePayload)
        .filter(key => !!userProfilePayload[key])
        .reduce((result, key) => ({ ...result, [key]: userProfilePayload[key] }), {});

    // Send request or resolve right away if no data to be submitted
    return (Object.keys(filteredPayload).length > 0)
        ? request.patch('/user/profile/', filteredPayload)
        : Promise.resolve({});
  }

  /**
   * Submit program data to the server.
   */
  private submitSelfEvaluations = () => {
    if (this.userType === UserType.MENTEE) {
      // Don't submit if in mentee mode - they are already individually posted
      return Promise.resolve({});
    }

    const programs = selfEvaluationEditorStore.selfEvaluations || [];
    return Promise.all(programs.map(selfEvaluation =>
        request.post('/self_evaluation_scores/', {
          program_topics: selfEvaluation.programId,
          score: selfEvaluation.score,
        })
    ));
  }
}

export default UserInfoStore;
export const userInfoStore = UserInfoStore.instance;


// WEBPACK FOOTER //
// ./src/stores/UserInfoStore.ts